import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { BASE_URL } from "../config";

const apiClient = axios.create({
  baseURL: BASE_URL,
});

// Request Interceptor: Automatically attach Bearer token from localStorage
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    try {
      const storedData = localStorage.getItem("loginData");
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        const accessToken = parsedData?.accessToken;
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
      }
    } catch (error) {
      console.error("Error reading auth token from localStorage", error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Concurrency handling for refresh token queueing
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response Interceptor: Global error & Seamless Silent Token Refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Don't attempt refresh if the failed request was login or refresh-token itself
    const isAuthEndpoint =
      originalRequest?.url?.includes("/auth/login") ||
      originalRequest?.url?.includes("/auth/refresh-token");

    const isUnauthorized =
      error.response?.status === 401 ||
      (error.response?.status === 403 &&
        String((error.response?.data as any)?.error?.message || "")
          .toLowerCase()
          .includes("un-authorized"));

    if (isUnauthorized && !originalRequest?._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // If already refreshing, queue this request until the new token arrives
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              resolve(apiClient(originalRequest));
            },
            reject: (err: any) => {
              reject(err);
            },
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const storedData = localStorage.getItem("loginData");
        const parsedData = storedData ? JSON.parse(storedData) : null;
        const refreshToken = parsedData?.refreshToken;

        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        // Call refresh endpoint directly using raw axios instance to prevent interceptor recursion
        const cleanBaseUrl = BASE_URL.replace(/\/+$/, "");
        const response = await axios.post(`${cleanBaseUrl}/api/v1/auth/refresh-token`, {
          token: refreshToken,
        });

        const newTokens = response.data?.data;
        const newAccessToken = newTokens?.accessToken;
        const newRefreshToken = newTokens?.refreshToken || refreshToken;

        if (!newAccessToken) {
          throw new Error("Invalid token refresh response from server");
        }

        // Update localStorage with fresh tokens
        const updatedLoginData = {
          ...parsedData,
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
        };
        localStorage.setItem("loginData", JSON.stringify(updatedLoginData));

        // Update Authorization header for original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        processQueue(null, newAccessToken);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Refresh token expired (> 7 days) or invalid -> clean up session
        localStorage.removeItem("loginData");
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("profile");

        if (typeof window !== "undefined" && !window.location.pathname.includes("/login")) {
          window.location.href = window.location.pathname.startsWith("/admin") ? "/admin" : "/login";
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
